import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import classNames from 'clsx';
import assetUrl from '../../utils/assetUrl';

export const defaultImageSrc = assetUrl('assets/img/default-item-v2.png');

/**
 *
 * @param src {string} URL that resolves image file
 * @param placeholderSrc {string} URL that resolves placeholder image file
 * @param alt {string} text description of image file
 * @param id {string} unique element id
 * @param dataTestId {string} unique test id for automation tests
 * @param style {object} CSS styles to be applied to element
 * @param containerClassName {string} class names to be applied to LazyLoad container
 * @param className {string} class names to be applied to element
 * @param onError {function} optional image error callback
 * @param renderImageWithCss {boolean} flag that enables CSS image loading over img tag, when true
 * @param disableLazyLoad {boolean} flag that disables image lazy loading
 */
const ImageWithDefault = ({
  src,
  placeholderSrc,
  alt,
  id,
  dataTestId,
  style = {},
  containerClassName,
  className,
  onError,
  renderImageWithCss = false,
  disableLazyLoad = false,
}) => {
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    setImageSrc(src);
  }, [src]);

  if (!imageSrc) return null;

  const placeholderImageSrc = placeholderSrc || defaultImageSrc;
  const placeholderImg = (
    <img
      id={`${id}-placeholderSrc`}
      data-testid={`${dataTestId}-placeholderSrc`}
      className={className}
      style={style}
      src={placeholderImageSrc}
      alt={alt}
    />
  );

  const image = (
    <>
      {renderImageWithCss && (
        <div
          id={`${id}-div`}
          data-testid={`${dataTestId}-div`}
          className={className}
          style={{
            ...style,
            backgroundImage: `url('${imageSrc}')`,
          }}
          role="img"
          aria-label={alt}
        />
      )}
      <img
        id={id}
        data-testid={dataTestId}
        className={classNames(className, { hide: renderImageWithCss })}
        style={style}
        src={imageSrc}
        alt={alt}
        onError={
          imageSrc === placeholderImageSrc
            ? null
            : () => {
                setImageSrc(placeholderImageSrc);
                onError?.();
              }
        }
      />
    </>
  );

  return disableLazyLoad ? (
    image
  ) : (
    <LazyLoad
      placeholder={placeholderImg}
      offset={100}
      overflow
      className={containerClassName}
    >
      {image}
    </LazyLoad>
  );
};

ImageWithDefault.propTypes = {
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  style: PropTypes.object,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  alt: PropTypes.string,
  placeholderSrc: PropTypes.string,
  src: PropTypes.string,
  renderImageWithCss: PropTypes.bool,
};

export default ImageWithDefault;
